import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const levels = [
    { name: 'Newbie', threshold: 0, icon: '🌱' },
    { name: 'Helper', threshold: 50, icon: '🙌' },
    { name: 'Champion', threshold: 150, icon: '🏆' },
    { name: 'Hero', threshold: 300, icon: '🦸' },
    { name: 'Legend', threshold: 500, icon: '🌟' }
];

const RewardsPage = ({ user }) => {
    const [redeemablePoints, setRedeemablePoints] = useState(user?.points || 0);
    const [reputationPoints, setReputationPoints] = useState(user?.impactPoints || 0);
    
    const [level, setLevel] = useState(levels[0]);
    const [nextLevel, setNextLevel] = useState(levels[1]);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        const currentReputation = user?.impactPoints || 0;
        const currentRedeemable = user?.points || 0;
        
        setReputationPoints(currentReputation);
        setRedeemablePoints(currentRedeemable);

        const currentLvl = levels.slice().reverse().find(l => currentReputation >= l.threshold) || levels[0];
        const nextLvl = levels.find(l => l.threshold > currentReputation);

        setLevel(currentLvl);
        
        if (nextLvl) {
            setNextLevel(nextLvl);
            const pointsInLevel = nextLvl.threshold - currentLvl.threshold;
            const pointsEarned = currentReputation - currentLvl.threshold;
            setProgress((pointsEarned / pointsInLevel) * 100);
        } else {
            setNextLevel(null);
            setProgress(100);
        }
    }, [user]);

    return (
        <motion.div
            key="rewards"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="p-4 md:p-8 max-w-4xl mx-auto"
        >
            <div className="text-center mb-10">
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Your Volunteer Status</h1>
                <p className="text-gray-600 mt-1">Track your impact and climb the ranks!</p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-200">
                <div className="flex flex-col md:flex-row justify-between items-center mb-6">
                    <div>
                        <p className="text-5xl font-bold">{level.icon}</p>
                        <h2 className="text-2xl font-bold text-gray-800 mt-2">{level.name}</h2>
                        <p className="text-gray-500">Current Level ({reputationPoints} RP)</p>
                    </div>
                    <div className="text-center md:text-right mt-4 md:mt-0">
                        <p className="text-4xl font-bold text-black">{redeemablePoints}</p>
                        <p className="text-gray-500">Redeemable Points</p>
                    </div>
                </div>

                <div>
                    <div className="flex justify-between items-center mb-2 text-sm font-medium">
                        <span className="text-gray-600">{level.threshold} RP</span>
                        {nextLevel ? (
                            <span className="text-gray-600">{nextLevel.threshold} RP</span>
                        ) : (
                             <span className="text-gray-600">Max Level</span>
                        )}
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <motion.div 
                            className="bg-black h-2.5 rounded-full" 
                            initial={{ width: 0 }}
                            animate={{ width: `${progress}%` }}
                            transition={{ duration: 1, ease: "easeOut" }}
                        />
                    </div>
                    <div className="text-center mt-3">
                        {nextLevel ? (
                             <p className="text-sm text-gray-600">
                                <span className="font-bold text-black">{nextLevel.threshold - reputationPoints}</span> Reputation Points to reach {nextLevel.name} {nextLevel.icon}
                            </p>
                        ) : (
                            <p className="font-semibold text-green-600">You've reached the highest level! 🌟</p>
                        )}
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default RewardsPage;
